/******************************************************************************
*
* Copyright (c) 2019-2025 ServiceActivator Inc. All Rights Reserved.
*
* Unauthorized copying of the code in this file, completely or partially,
* via any medium is strictly prohibited.
*
* Proprietary and Confidential.
*
* Written by 'Kunal Pandey' <kunal.pandey@wakencode.com>, February 2025
*
*****************************************************************************/

import React, { useEffect, useState } from "react";
import Styles from "./OwnerDetails.module.css";
import { prepareOwnerSubscriberDetails } from "../../container/postDataFormat";
import { updateInfo } from "../../network";
import { string } from "../../constant/string";
import Loader from "../../loader/WhiteLoader";
import { useAuth } from "../../context/AuthContext";
import { useUser } from "../../context/UserContext";
import { SALoader } from "../../loader/SALoader";
import NumberFormat from "react-number-format";

/**
 * Owner details page called in Wrapper.js
 * This page takes owner details and updates in api
 *
 * @param {String} setPage State for current page
 * @param {String} jobRole State for job role
 * @param {String} setJobRole Method to update state for job role
 * @param {Boolean} isInvited State for user invitaion
 * @param {Function} setIsInvited setState for user invitation
 * @returns JSX for the personal details/your details page
 */
const OwnerDetails = ({ setPage, jobRole, setJobRole, isInvited, setIsInvited }) => {
  // State for loader
  const [loading, setLoading] = useState(false);
  // State for activation loader
  const [pageLoading, setPageLoading] = useState(false);
  // State for user info
  const [ownerInfo, setOwnerInfo] = useState({
    firstName: "",
    lastName: "",
    email: "",
    mobile: "",
  });
  const [isValid, setIsValid] = useState(false);

  // State to set token from auth context
  const { token } = useAuth("");
  //State to set user data from user context
  const { userData, setUserData } = useUser("");

  // State for user input error info
  const [ownerInfoError, setOwnerInfoError] = useState({
    firstName: "",
    lastName: "",
    email: "",
    mobile: "",
    credentials: "",
    saveUserData: "",
  });

  const config = {
    headers: { Authorization: `Bearer ${token}` },
  };

  /**
   * Prepopulate data on screen
   */
  useEffect(() => {
    setOwnerInfo((prev) => ({
      ...prev,
      firstName: userData?.subscriberOwnerDetails?.firstName,
      lastName: userData?.subscriberOwnerDetails?.lastName,
      email: userData?.subscriberOwnerDetails?.email,
      mobile: userData?.subscriberOwnerDetails?.mobile,
    }));
  }, []);

  useEffect(() => {
    if (isValid) {
      callApiToSaveData();
      setIsValid(false);
    }
  }, [userData, isValid]);

  /**
   * Handle input change
   *
   * @param {MouseEvent} e Input change event
   */
  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Set user information
    setOwnerInfo((prevState) => {
      return {
        ...prevState,
        [name]: value,
      };
    });
    // Set error for user information
    setOwnerInfoError((prevState) => {
      return {
        ...prevState,
        [name]: "",
      };
    });
  };

  /**
   * Validate user entered email
   *
   * @param {email} email User entered email id
   * @returns {boolean} Valid email or not
   */
  const validateEmail = (email) => {
    let format = /\S+@\S+\.\S+/;
    return format.test(email);
  };

  /**
   * Call update api to save all the user entered information
   */
  const callApiToSaveData = () => {
    setLoading(true);
    console.log(userData, ownerInfo, "updated user data");

    // Prepare correct format for request body
    let postData = prepareOwnerSubscriberDetails(userData, ownerInfo);
    // Call api to update/save user information
    updateInfo(postData, config)
      .then(() => {
        setPage("ActivatingAccount");
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        if (err?.response?.status === 500) {
          setApiError("saveUserData", string.SOMETHING_WENT_WRONG_TRY_AGAIN);
        }else if (err?.response?.status === 401){
          setPage("SigUp")
        } else if (err.response && err.response.data && err.response.data.errorDescription) {
          setApiError("saveUserData", err.response.data.errorDescription);
        }
      });
  };

  /**
   * Update error state for invalid/empty user input
   *
   * @param {String} name User information object key
   * @param {*} value User information object value
   */
  const setApiError = (name, value) => {
    setOwnerInfoError((prevState) => {
      return {
        ...prevState,
        [name]: value,
      };
    });
  };

  /**
   * Validate user information if valid or not
   *
   * @returns {Boolean} User input are valid or not
   */
  const validateAllInput = () => {
    let flag = true;
    if (!ownerInfo.firstName.trim()) {
      flag = false;
      // Set first name error
      setApiError("firstName", string.REQUIRED);
    }
    if (!ownerInfo.lastName.trim()) {
      flag = false;
      // Set last name error
      setApiError("lastName", string.REQUIRED);
    }

    if (!ownerInfo.mobile && !ownerInfo.email) {
      // Set credentials required error
      flag = false;
      setApiError("credentials", string.CREDENTIALS_REQUIRED);
    }

    if (ownerInfo.email) {
      let emailValid = validateEmail(ownerInfo.email);
      if (!emailValid) {
        flag = false;
        // Set email id error
        setApiError("email", string.ENTER_VALID_EMAIL);
      } else if (ownerInfo.email.toLowerCase() === userData?.userDetails?.email?.toLowerCase()) {
        flag = false;
        // Owner email cannot match employee email
        setApiError("email", string.OWNER_EMAIL_SAME_AS_EMPLOYEE);
      }
    }

    if (ownerInfo.mobile) {
      let mobileNumberWithoutPrefix = ownerInfo.mobile.substring(2, ownerInfo.mobile.length).replace(/ /g, "");
      // If user entered invalid mobile number update error state
      if (mobileNumberWithoutPrefix.length > 0 && mobileNumberWithoutPrefix.length < 10) {
        flag = false;
        setApiError("mobile", string.NUMBER_MUST_BE_10_DIGIT);
      }
    }

    if (ownerInfoError.saveUserData) {
      //if user post api failed at this point(page), do not allow to proceed further
      flag = false;
    }

    return flag;
  };

  /**
   * Next button handler
   *
   * @param {*} e Event for Next button click
   */
  const handleNextButtonChange = (e) => {
    e.preventDefault();

    setIsValid(validateAllInput());

    let mobile = ownerInfo.mobile;

    mobile = mobile.split(" ").join("")

    // Save user information to local storage
    setUserData((prev) => ({
      ...prev,
      subscriberOwnerDetails: {
        ...(prev?.subscriberOwnerDetails || {}),
        firstName: ownerInfo.firstName,
        lastName: ownerInfo.lastName,
        email: ownerInfo.email,
        mobile: mobile,
      },
    }));

    setOwnerInfo((prev) => ({ ...prev, mobile: ownerInfo.mobile.split(" ").join("") }));

    console.log(userData, ownerInfo);
  };

  /**
   * Back button handler
   *
   * @param {*} e Event for Back button click
   */
  const handleBackButtonChange = (e) => {
    e.preventDefault();
    setPage("WorkSizeDetails");
  };

  return (
    <>
      {pageLoading ? (
        <div className="adjustLoaderForFullScreen">
          <SALoader />
        </div>
      ) : (
        <div className="main">
          {/* Head text */}
          <div className="headText">{string.OWNER_DETAILS}</div>
          {/* Line under head text */}
          <div className="line"></div>
          {/* Sub-heading under line */}
          <div className="text">{string.ENTER_OWNER_DETAILS} </div>
          {/* input fields for user details */}
          <div className="flexContainer">
            {/* First name input field */}
            <div className="tenREM">
              <input
                className={Styles.firstName}
                onChange={handleInputChange}
                placeholder={string.FIRST_NAME}
                name="firstName"
                value={ownerInfo.firstName}
                id={ownerInfoError.firstName ? "redBorder" : "greyBorder"}
              ></input>
              {ownerInfoError.firstName ? <p className="errorText"> {ownerInfoError.firstName} </p> : null}
            </div>
            {/* Last name input field */}
            <div className="tenREM">
              <input
                className={Styles.lastName}
                onChange={handleInputChange}
                placeholder={string.LAST_NAME}
                name="lastName"
                value={ownerInfo.lastName}
                id={ownerInfoError.lastName ? "redBorder" : "greyBorder"}
              ></input>
              {ownerInfoError.lastName ? <p className="errorText"> {ownerInfoError.lastName} </p> : null}
            </div>
          </div>
          {/* Email id input field */}
          <div className="tenREM">
            <input
              onChange={handleInputChange}
              type="email"
              placeholder={string.EMAIL_ADDRESS}
              name="email"
              value={ownerInfo.email}
              className={ownerInfoError.email ? "wrongInput" : "input"}
            ></input>
            {ownerInfoError.email && ownerInfoError.email.includes("\n") && (
              <p className="errorTextModifiedForEmailError">
                {" "}
                {ownerInfoError.email.split("\n").map((line, index) => (
                  <React.Fragment key={index}>
                    {line}
                    <br />
                  </React.Fragment>
                ))}
              </p>
            )}
            {ownerInfoError.email && !ownerInfoError.email.includes("\n") && (
              <p className="errorText"> {ownerInfoError.email}</p>
            )}
          </div>
          {/* Mobile input field */}
          <div className="tenREM">
            <NumberFormat
              onChange={handleInputChange}
              className={ownerInfoError.mobile ? "wrongInput" : "input"}
              value={ownerInfo.mobile}
              name="mobile"
              placeholder={string.PHONE_NUMBER}
              type="Tel"
              format="+1 ### ### ####"
            ></NumberFormat>
            {ownerInfoError.mobile ? <p className="errorText">{ownerInfoError.mobile}</p> : null}
            {ownerInfoError.credentials ? <p className="errorText">{ownerInfoError.credentials}</p> : null}
            {ownerInfoError.saveUserData ? <p className="errorText">{ownerInfoError.saveUserData}</p> : null}
          </div>
          {/* Next button */}
          <div>
            <button className="nextButton" onClick={!loading ? handleNextButtonChange : null}>
              {" "}
              {loading ? <Loader /> : string.NEXT}
            </button>
          </div>
          {/* Back button */}
          <div>
            <button className="backButton" onClick={handleBackButtonChange}>
              {string.BACK}
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default OwnerDetails;
